import React from "react";
import styled, { keyframes } from "styled-components";
import { useLayer } from "./LayerContext";
import { layerIDs } from "./layerIDs";
import ReactLogo from "./../static/Logo_SSR_4_web_poziome.svg";
import { BIKE_WAY_FILTER_OPTIONS, getBikeWayLegendStyle } from "../services/bicycle_ways";

type ILayerElement = {
  id: string,
  label: string,
  completed: boolean
}

type MapLayersProps = {
  bikeWayLoading: Record<string, boolean>;
  bikeWayVisibility: Record<string, boolean>;
  onToggleBikeWayVisibility: (bikeWayClass: string) => void;
};

const LayerPanel = styled.div`
  margin-top: 12px;
  padding: 0 12px;
  width: 260px;
`;

const LayerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const LogoImage = styled.img`
  width: 100%;
  max-width: 220px;
  height: auto;
  display: block;
  margin-bottom: 8px;
`;

const MapTitle = styled.p`
  margin: 0 0 8px;
  font-size: 13px;
  line-height: 1.1;
  white-space: nowrap;
`;

const spinAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`;

const LayerButton = styled.button<{ $active: boolean, $disabled: boolean, $expanded?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: ${({ $expanded }) => ($expanded ? "8px 8px 0 0" : "8px")};
  border: 1px solid ${({ $active, $disabled }) => ($disabled ? "#d8d8d8" : $active ? "#1f1f1f" : "#cfcfcf")};
  background: ${({ $active, $disabled }) => ($disabled ? "#f2f2f2" : $active ? "#111111" : "#ffffff")};
  color: ${({ $active, $disabled }) => ($disabled ? "#9d9d9d" : $active ? "#ffffff" : "#1a1a1a")};
  box-shadow: ${({ $active }) =>
    $active ? "0 6px 16px rgba(0, 0, 0, 0.18)" : "0 2px 8px rgba(0, 0, 0, 0.08)"};
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;
  text-align: left;
  cursor: ${({ $disabled }) => ($disabled ? "wait" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.85 : 1)};

  &:hover {
    border-color: ${({ $disabled }) => ($disabled ? "#d8d8d8" : "#111111")};
    box-shadow: ${({ $disabled }) => ($disabled ? "0 2px 8px rgba(0, 0, 0, 0.06)" : "0 6px 16px rgba(0, 0, 0, 0.14)")};
  }
`;

const LayerIconTile = styled.span<{ $active: boolean, $disabled: boolean }>`
  width: 34px;
  height: 34px;
  min-width: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 7px;
  background: ${({ $active, $disabled }) => ($disabled ? "#ebebeb" : $active ? "#ffffff" : "#f5f5f5")};
  border: 1px solid ${({ $active, $disabled }) => ($disabled ? "#d1d1d1" : $active ? "#ffffff" : "#d7d7d7")};
  overflow: hidden;
`;

const LayerIconImage = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
  display: block;
`;

const LayerIconFallback = styled.span<{ $active: boolean }>`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #1d1d1d;
  opacity: ${({ $active }) => ($active ? 1 : 0.85)};
`;

const LayerLabel = styled.span`
  font-size: 14px;
  line-height: 1.2;
  flex: 1 1 auto;
`;

const LoadingSpinner = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid #b6b6b6;
  border-top-color: #5a5a5a;
  animation: ${spinAnimation} 0.8s linear infinite;
`;

const LayerSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const ExpandToggle = styled.button<{ $active: boolean, $expanded: boolean }>`
  width: 26px;
  height: 26px;
  min-width: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? "rgba(255, 255, 255, 0.14)" : "#f0f0f0")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#404040")};
  transform: rotate(${({ $expanded }) => ($expanded ? "90deg" : "0deg")});
  transition: transform 0.15s ease, background 0.15s ease;
  cursor: pointer;
`;

const SubLayerPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 8px;
  border: 1px solid #cfcfcf;
  border-top: 0;
  border-radius: 0 0 8px 8px;
  background: #fafafa;
`;

const SubLayerButton = styled.button<{ $active: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  border: 1px solid ${({ $active }) => ($active ? "#1f1f1f" : "#d5d5d5")};
  border-radius: 7px;
  background: ${({ $active }) => ($active ? "#ffffff" : "#f3f3f3")};
  color: #202020;
  text-align: left;
  cursor: pointer;
`;

const SubLayerStatus = styled.span`
  width: 18px;
  min-width: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const SubLayerLineSample = styled.span<{
  $color: string;
  $width: number;
  $dasharray: string | null;
  $visible: boolean;
}>`
  width: 34px;
  min-width: 34px;
  height: ${({ $width }) => `${Math.max(3, Math.min(6, $width))}px`};
  border-radius: 999px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0.28)};
  background: ${({ $color, $dasharray }) => {
    if (!$dasharray) return $color;

    const [dash, gap] = $dasharray.split(",").map(Number);
    const dashLength = Number.isFinite(dash) ? dash : 8;
    const gapLength = Number.isFinite(gap) ? gap : 4;
    return `repeating-linear-gradient(90deg, ${$color} 0 ${dashLength}px, transparent ${dashLength}px ${dashLength + gapLength}px)`;
  }};
`;

const SubLayerDot = styled.span<{ $active: boolean }>`
  width: 10px;
  height: 10px;
  min-width: 10px;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? "#0b7a4e" : "#c2c2c2")};
`;

const SubLayerLabel = styled.span`
  font-size: 12px;
  line-height: 1.25;
`;

const mapLayers = layerIDs.filter((layer) => layer.group === "map");

const MapLayers: React.FC<MapLayersProps> = ({
  bikeWayLoading,
  bikeWayVisibility,
  onToggleBikeWayVisibility,
}) => {
  const { layersList, addLayer, removeLayer, layerStatus } = useLayer();
  const [expandedWays, setExpandedWays] = React.useState(false);
  const flatLayersList: React.MutableRefObject<string[] | undefined> = React.useRef();
  const isAnyBikeWayLoading = Object.values(bikeWayLoading).some(Boolean);

  flatLayersList.current = layersList.map((layer: ILayerElement) => layer.id);

  React.useEffect(() => {
    if (isAnyBikeWayLoading) {
      setExpandedWays(true);
    }
  }, [isAnyBikeWayLoading]);

  return (
    <LayerPanel>
      <LogoImage src={ReactLogo} alt="" />
      <div className="d-flex flex-column justify-content-*-start align-items-*-start">
        <MapTitle>Rowerowa mapa Sosnowca:</MapTitle>
      </div>
      <LayerList>
        {mapLayers.map((layer, index) => {
          const isActive = flatLayersList.current?.includes(layer.id) ?? false;
          const isWaysLayer = layer.id === "ways";
          const isLoading = Boolean(layerStatus?.[layer.id]?.loading);
          const isExpanded = isWaysLayer && expandedWays;

          return (
            <LayerSection key={index}>
              <LayerButton
                $active={isActive}
                $disabled={isLoading}
                $expanded={isExpanded}
                disabled={isLoading}
                onClick={() => {
                  if (isLoading) {
                    return;
                  }

                  if (isWaysLayer) {
                    setExpandedWays((current) => !current);
                    if (!isActive) {
                      addLayer(layer.id);
                    }
                    return;
                  }

                  if (isActive) {
                    removeLayer(layer.id);
                  } else {
                    addLayer(layer.id);
                  }
                }}
              >
                <LayerIconTile $active={isActive} $disabled={isLoading}>
                  {isLoading ? (
                    <LoadingSpinner aria-hidden="true" />
                  ) : layer.icon ? (
                    <LayerIconImage src={layer.icon} alt="" />
                  ) : (
                    <LayerIconFallback $active={isActive}>{layer.symbol}</LayerIconFallback>
                  )}
                </LayerIconTile>
                <LayerLabel>{layer.name}</LayerLabel>
                {isWaysLayer ? (
                  <ExpandToggle
                    type="button"
                    $active={isActive}
                    $expanded={expandedWays}
                    aria-label={expandedWays ? "Zwiń klasy dróg rowerowych" : "Rozwiń klasy dróg rowerowych"}
                    onClick={(event) => {
                      event.stopPropagation();
                      setExpandedWays((current) => !current);
                      if (!isActive) {
                        addLayer(layer.id);
                      }
                    }}
                  >
                    ›
                  </ExpandToggle>
                ) : null}
              </LayerButton>

              {isWaysLayer && expandedWays ? (
                <SubLayerPanel>
                  <SubLayerButton
                    type="button"
                    $active={isActive}
                    onClick={() => {
                      if (isActive) {
                        removeLayer(layer.id);
                      } else {
                        addLayer(layer.id);
                      }
                    }}
                  >
                    <SubLayerDot $active={isActive} />
                    <SubLayerLabel>widoczność wszystkich dróg rowerowych</SubLayerLabel>
                  </SubLayerButton>

                  {BIKE_WAY_FILTER_OPTIONS.map((option) => (
                    <SubLayerButton
                      key={option.id}
                      type="button"
                      $active={bikeWayVisibility[option.id] !== false}
                      disabled={bikeWayLoading[option.id] === true}
                      onClick={() => onToggleBikeWayVisibility(option.id)}
                    >
                      {(() => {
                        const style = getBikeWayLegendStyle(option.id);
                        const isVisible = bikeWayVisibility[option.id] !== false;

                        return (
                          <SubLayerLineSample
                            $color={style.color}
                            $width={style.width}
                            $dasharray={style.dasharray}
                            $visible={isVisible}
                            aria-hidden="true"
                          />
                        );
                      })()}
                      <SubLayerStatus>
                        {bikeWayLoading[option.id] === true ? (
                          <LoadingSpinner aria-hidden="true" />
                        ) : (
                          <SubLayerDot $active={bikeWayVisibility[option.id] !== false} />
                        )}
                      </SubLayerStatus>
                      <SubLayerLabel>{option.label}</SubLayerLabel>
                    </SubLayerButton>
                  ))}
                </SubLayerPanel>
              ) : null}
            </LayerSection>
          );
        })}
      </LayerList>
    </LayerPanel>
  );
};

export default MapLayers;
